import type { ButtonSize } from '@ds/components/actions/Button';
import IconButton, { type IconButtonVariant } from '@ds/components/actions/IconButton';
import Tooltip from '@ds/components/feedback/Tooltip';
import { useTheme } from '@ds/hooks/useTheme';
import { DarkModeIcon, LightModeIcon } from '@ds/icons';
import React, { forwardRef } from 'react';

export interface ThemeToggleProps {
    /**
     * Visual priority of the control. Defaults to `text`, the quiet treatment for a utility that
     * sits beside the user menu rather than competing with page actions.
     */
    variant?: IconButtonVariant;
    /** Square size: `sm` is 40×40, `lg` is 56×56. */
    size?: ButtonSize;
    className?: string;
}

/**
 * Switches the design-system theme between light and dark.
 *
 * An icon-only button whose glyph shows the theme you would switch **to**: a moon while the
 * page is light, a sun while it is dark. The accessible name says the same ("Switch to dark
 * theme"), and a tooltip repeats it on hover and focus. Clicking stores an explicit `light` or
 * `dark` preference through `useTheme`, so the choice persists across reloads and tabs and the
 * `data-theme` attribute on `<html>` updates before the next paint.
 *
 * It is a two-way toggle on the *resolved* theme, so a user whose OS is dark and who has never
 * chosen sees a sun and gets `light` on the first click. It does not offer "follow the system
 * setting" — that would need a third state the header has no room to explain; `useTheme`'s
 * `setTheme('system')` is available for a settings surface later.
 *
 * @example
 * <ThemeToggle />
 */
const ThemeToggle = forwardRef<HTMLButtonElement, ThemeToggleProps>(
    ({ variant = 'text', size = 'sm', className }, ref) => {
        const { resolvedTheme, setTheme } = useTheme();
        const isDark = resolvedTheme === 'dark';
        const label = isDark ? 'Switch to light theme' : 'Switch to dark theme';

        return (
            <Tooltip
                text={label}
                placement="bottom"
            >
                <IconButton
                    ref={ref}
                    icon={isDark ? <LightModeIcon /> : <DarkModeIcon />}
                    label={label}
                    variant={variant}
                    size={size}
                    shape="circle"
                    className={className}
                    data-resolved-theme={resolvedTheme}
                    onClick={() => setTheme(isDark ? 'light' : 'dark')}
                />
            </Tooltip>
        );
    }
);

ThemeToggle.displayName = 'ThemeToggle';

export { ThemeToggle };
export default ThemeToggle;
